import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BookOpen, ChartBar, Users, ChatCircle, Truck, House, Shield,
  ArrowRight, List, X, CaretDown, Check, Star,
  GraduationCap, ChalkboardTeacher, UserCircle, Buildings,
  TwitterLogo, LinkedinLogo, FacebookLogo,
} from 'phosphor-react';
import { LogoWithText } from '../components/Logo';
import useMetaTags from '../hooks/useMetaTags';
import './LandingPage.css';

/* ── Scroll animation observer ─────────────────────────────────────────── */
function useScrollReveal() {
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => entries.forEach(e => { if (e.isIntersecting) e.target.classList.add('in-view'); }),
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    document.querySelectorAll('.lp-fade,.lp-fade-left,.lp-fade-right')
      .forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
}

/* ── Animated counter ──────────────────────────────────────────────────── */
function Counter({ target, suffix = '', duration = 1800 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        const start = Date.now();
        const tick = () => {
          const elapsed = Date.now() - start;
          const p = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - p, 3);
          setCount(Math.round(eased * target));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

/* ── Image with fallback ──────────────────────────────────────────────── */
function Img({ src, alt, className, style }) {
  const [errored, setErrored] = useState(false);
  if (errored) {
    return (
      <div
        className={className}
        style={{ ...style, background: '#3D5C3A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <GraduationCap size={64} color="rgba(255,255,255,0.2)" />
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} style={style} onError={() => setErrored(true)} />;
}

/* ─────────────────────────────────────────────────────────────────────────── */

const FEATURES = [
  {
    icon: BookOpen,
    title: 'Academic Management',
    description: 'Student records, attendance, grades, timetables and assignments — all in one place, built for how schools actually work.',
    tags: ['Student Records', 'Attendance', 'Grade Books', 'Timetables'],
  },
  {
    icon: ChartBar,
    title: 'Finance & Accounting',
    description: 'Double-entry bookkeeping, expense tracking, and financial reporting designed for school finance offices.',
    tags: ['Chart of Accounts', 'Journal Entries', 'Reports', 'Budgets'],
  },
  {
    icon: Truck,
    title: 'Transport Management',
    description: 'Manage routes, vehicles, drivers, and student boarding with live location sharing for parents.',
    tags: ['Fleet', 'Route Planning', 'Live GPS', 'Student Boarding'],
  },
  {
    icon: House,
    title: 'Hostel Operations',
    description: 'Room allocation, attendance, visitor logs, complaints and maintenance tracking for boarding schools.',
    tags: ['Room Allocation', 'Visitors', 'Complaints', 'Maintenance'],
  },
  {
    icon: ChatCircle,
    title: 'Communication',
    description: 'Connect parents, teachers, and admin through direct messaging and targeted SMS campaigns.',
    tags: ['Parent Messaging', 'SMS Blasts', 'Announcements', 'Fee Alerts'],
  },
  {
    icon: Shield,
    title: 'Student Safety',
    description: 'QR-based pickup verification, collector tracking, and real-time status updates so parents always know their child is safe.',
    tags: ['QR Verification', 'Live Tracking', 'Pickup Alerts', 'Gate Logs'],
  },
];

const ROLES = [
  {
    icon: Buildings,
    title: 'School Administrators',
    description: 'Full visibility across every department from one dashboard.',
    items: ['Budget oversight', 'Staff management', 'School configuration', 'Bulk data import'],
  },
  {
    icon: ChalkboardTeacher,
    title: 'Teachers',
    description: 'Teaching tools that reduce paperwork and keep you in the classroom.',
    items: ['Attendance marking', 'Grade entry', 'Assignment management', 'Student reports'],
  },
  {
    icon: UserCircle,
    title: 'Parents',
    description: 'Stay informed about your child at every step of the school day.',
    items: ['Fee payments', 'Live pickup tracking', 'Progress reports', 'Direct teacher chat'],
  },
  {
    icon: GraduationCap,
    title: 'Students',
    description: 'Your own portal for tracking grades, assignments, and announcements.',
    items: ['Grade tracking', 'Assignment submissions', 'Performance analytics', 'Announcements'],
  },
];

const TESTIMONIALS = [
  {
    name: 'David Mensah',
    role: 'School Principal',
    school: 'Accra International School',
    content: 'Campusio changed how we operate. Reports that took three days are ready in minutes. Our staff actually enjoy using it.',
    rating: 5,
  },
  {
    name: 'Ama Osei',
    role: 'Finance Manager',
    school: 'Valley Academy',
    content: 'The accounting module is proper double-entry, not just a spreadsheet dressed up. Our audits are now clean every term.',
    rating: 5,
  },
  {
    name: 'Kwasi Boateng',
    role: 'Class Teacher',
    school: 'Heritage School',
    content: 'Marking attendance used to eat into my lesson time. Now it takes thirty seconds. That time goes back to my students.',
    rating: 5,
  },
];

const TABLE_ROWS = [
  { label: 'Real-time data',       legacy: 'no',  tools: 'no',      us: 'yes' },
  { label: 'Automated reporting',  legacy: 'no',  tools: 'partial', us: 'yes' },
  { label: 'Role-based portals',   legacy: 'no',  tools: 'partial', us: 'yes' },
  { label: 'Mobile-ready',         legacy: 'no',  tools: 'no',      us: 'yes' },
  { label: 'Student safety tools', legacy: 'no',  tools: 'no',      us: 'yes' },
  { label: 'Financial compliance', legacy: 'partial', tools: 'partial', us: 'yes' },
  { label: 'Multi-school support', legacy: 'no',  tools: 'no',      us: 'yes' },
];

const FAQS = [
  { q: 'How quickly can my school get started?', a: 'Most schools are live within 24–48 hours. Our team handles setup, data import, and staff training.' },
  { q: 'Can we import our existing student records?', a: 'Yes. We provide CSV templates and can help migrate data from spreadsheets or other systems.' },
  { q: 'Is our data secure?', a: 'Your data is encrypted at rest and in transit, backed up daily, and stored on servers that meet international data protection standards.' },
  { q: 'What payment methods do you accept?', a: 'We accept bank transfers and mobile money payments via Paystack. No card required to start your trial.' },
  { q: 'Is there a free trial?', a: 'Yes — 30 days, full access, no credit card needed.' },
  { q: 'What support do you offer?', a: 'Email support available every day, phone support during business hours, and a full documentation library with video walkthroughs.' },
];

const SCHOOLS = ['Accra International', 'Valley Academy', 'Heritage School', 'Presec', 'Kolbe School', 'Opoku Ware', 'Mfantsipim', 'Wesley Girls'];

/* hero images — replace with your own photos of African school children */
const HERO_IMG   = 'https://images.unsplash.com/photo-1509062522246-3755977927d7?auto=format&fit=crop&w=1400&q=80';
const STORY_IMG1 = 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?auto=format&fit=crop&w=900&q=80';
const STORY_IMG2 = 'https://images.unsplash.com/photo-1491499442783-a4eecdf68f0d?auto=format&fit=crop&w=900&q=80';

/* ─────────────────────────────────────────────────────────────────────────── */

export default function LandingPage() {
  const navigate = useNavigate();
  const [scrolled, setScrolled]   = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);
  const [openFaq,  setOpenFaq]    = useState(null);

  useMetaTags({
    title: "Campusio — Enterprise Grade School ERP for Africa",
    description: "One unified enterprise-grade school ERP for academics, finance, transport, hostels, and communication. Built for African schools.",
  });

  useScrollReveal();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  const scrollTo = useCallback((id) => {
    setMenuOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const TableIcon = ({ value }) => {
    if (value === 'yes') return <Check size={18} weight="bold" className="lp-table-yes" style={{ margin: '0 auto' }} />;
    if (value === 'no')  return <X size={18} weight="bold" className="lp-table-no" style={{ margin: '0 auto' }} />;
    return <CaretDown size={16} className="lp-table-partial" style={{ margin: '0 auto', transform: 'rotate(-90deg)' }} />;
  };

  return (
    <div style={{ background: 'var(--land-white)', color: 'var(--land-text)', fontFamily: "'IBM Plex Sans', sans-serif" }}>

      {/* ── Nav ─────────────────────────────────────────────────────── */}
      <nav className={`lp-nav${scrolled ? ' scrolled' : ''}`}>
        <div className="lp-nav-inner">
          <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <LogoWithText size={30} />
          </button>

          <div className="lp-nav-links">
            {[['features','Features'], ['story','How It Works'], ['roles','Who It\'s For'], ['testimonials','Testimonials']].map(([id, label]) => (
              <button key={id} className="lp-nav-link" onClick={() => scrollTo(id)}>{label}</button>
            ))}
            <button className="lp-nav-link" onClick={() => navigate('/pricing')}>Pricing</button>
            <button className="lp-nav-link" onClick={() => navigate('/documentation')}>Docs</button>
            <button className="lp-nav-link" onClick={() => navigate('/contact')}>Contact</button>
          </div>

          <div className="lp-nav-actions">
            <button className="lp-btn-ghost" onClick={() => navigate('/login')}>Sign In</button>
            <button className="lp-btn-primary" onClick={() => navigate('/contact')}>
              Request a Demo <ArrowRight size={15} weight="bold" />
            </button>
          </div>

          <button
            onClick={() => setMenuOpen(true)}
            style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
            className="lp-hamburger"
          >
            <List size={26} color="var(--land-text)" />
          </button>
        </div>
      </nav>

      {/* ── Mobile menu ──────────────────────────────────────────────── */}
      {menuOpen && (
        <div className="lp-mobile-menu">
          <button className="lp-mobile-close" onClick={() => setMenuOpen(false)}>
            <X size={20} />
          </button>
          {[['features','Features'],['story','How It Works'],['roles','Who It\'s For'],['testimonials','Testimonials']].map(([id, label]) => (
            <button key={id} className="lp-mobile-link" onClick={() => scrollTo(id)}>{label}</button>
          ))}
          <button className="lp-mobile-link" onClick={() => { navigate('/pricing'); setMenuOpen(false); }}>Pricing</button>
          <button className="lp-mobile-link" onClick={() => { navigate('/documentation'); setMenuOpen(false); }}>Documentation</button>
          <button className="lp-mobile-link" onClick={() => { navigate('/contact'); setMenuOpen(false); }}>Contact</button>
          <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 24, borderTop: '1px solid var(--land-border)' }}>
            <button className="lp-btn-outline-lg" style={{ justifyContent: 'center' }} onClick={() => { navigate('/login'); setMenuOpen(false); }}>Sign In</button>
            <button className="lp-btn-primary-lg" style={{ justifyContent: 'center' }} onClick={() => { navigate('/contact'); setMenuOpen(false); }}>Request a Demo</button>
          </div>
        </div>
      )}

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="lp-hero">
        <div className="lp-hero-left">
          <div className="lp-hero-badge lp-fade">
            <Shield size={13} weight="fill" />
            Enterprise Grade School ERP
          </div>

          <h1 className="lp-hero-h1 lp-fade lp-delay-1">
            Every great school runs on the <span>right system.</span>
          </h1>

          <p className="lp-hero-sub lp-fade lp-delay-2">
            Campusio brings academics, finance, transport, and communication onto a single platform — purpose-built for African schools.
          </p>

          <div className="lp-hero-ctas lp-fade lp-delay-3">
            <button className="lp-btn-primary-lg" onClick={() => navigate('/contact')}>
              Request a Demo <ArrowRight size={18} weight="bold" />
            </button>
            <button className="lp-btn-outline-lg" onClick={() => scrollTo('features')}>
              See What's Inside
            </button>
          </div>

          <div className="lp-hero-trust lp-fade lp-delay-4">
            <div className="lp-hero-trust-dot" />
            Trusted by 120+ schools across Ghana and West Africa
          </div>
        </div>

        <div className="lp-hero-right">
          <Img src={HERO_IMG} alt="Students in school" className="lp-hero-img" />
          <div className="lp-hero-overlay" />
          <div className="ml-8 lp-hero-card">
            <div className="lp-hero-card-num">50K+</div>
            <div className="lp-hero-card-label">Students managed</div>
          </div>
          <div className=" mt-8 lp-hero-card2">
            <div className="lp-hero-card2-num">99.9%</div>
            <div className="lp-hero-card2-label">Uptime guaranteed</div>
          </div>
        </div>
      </section>

      {/* ── Trust bar ────────────────────────────────────────────────── */}
      <div className="lp-trust">
        <div className="lp-trust-inner" aria-hidden>
          {[...SCHOOLS, ...SCHOOLS].map((s, i) => (
            <span key={i} className="lp-trust-item">{s}</span>
          ))}
        </div>
      </div>

      {/* ── Features ─────────────────────────────────────────────────── */}
      <section id="features" style={{ background: 'var(--land-off)', padding: '0 0 0 0' }}>
        <div className="lp-section">
          <div style={{ marginBottom: 56 }}>
            <p className="lp-section-label lp-fade">Platform Modules</p>
            <h2 className="lp-section-h2 lp-fade lp-delay-1">One system. Everything your school needs.</h2>
            <p className="lp-section-sub lp-fade lp-delay-2">
              Purpose-built modules that work together — not a patchwork of disconnected tools.
            </p>
          </div>

          <div className="lp-features-grid">
            {FEATURES.map((f, i) => {
              const Icon = f.icon;
              return (
                <div key={i} className={`lp-feature-card lp-fade lp-delay-${Math.min(i + 1, 5)}`}>
                  <div className="lp-feature-icon">
                    <Icon size={22} weight="duotone" />
                  </div>
                  <div className="lp-feature-title">{f.title}</div>
                  <div className="lp-feature-desc">{f.description}</div>
                  <div className="lp-feature-status" style={{ marginBottom: 12, display: 'inline-flex', alignItems: 'center', padding: '6px 10px', borderRadius: 999, background: '#ecfdf5', color: '#166534', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                    Available
                  </div>
                  <div className="lp-feature-tags">
                    {f.tags.map(t => <span key={t} className="lp-feature-tag">{t}</span>)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <hr className="lp-divider" style={{ margin: 0 }} />

      {/* ── Photo story 1 ────────────────────────────────────────────── */}
      <section id="story" style={{ background: 'white', padding: '80px 32px' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 24 }}>
          <div className="lp-story lp-fade">
            <Img src={STORY_IMG1} alt="Students learning together" className="lp-story-img" />
            <div className="lp-story-content">
              <p className="lp-section-label">Academics</p>
              <h3 className="lp-story-title">Built for the classroom, not the boardroom.</h3>
              <p className="lp-story-body">
                Attendance in one tap. Grades entered once and reflected instantly in student and parent reports. No double-entry, no end-of-term panic.
              </p>
              <ul className="lp-check-list">
                {['Mark attendance on any device', 'Grade books with class averages and rankings', 'Timetables visible to teachers, students, and parents', 'Bulk report card generation'].map(item => (
                  <li key={item}>
                    <Check size={16} weight="bold" className="icon" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lp-story flip lp-fade">
            <Img src={STORY_IMG2} alt="School children" className="lp-story-img" />
            <div className="lp-story-content">
              <p className="lp-section-label">Safety</p>
              <h3 className="lp-story-title">Parents know exactly when their child gets home.</h3>
              <p className="lp-story-body">
                Our QR-based pickup system verifies that the right person collects each student, then tracks the collector's live location until the child is confirmed safe at home.
              </p>
              <ul className="lp-check-list">
                {['QR tokens verified at the school gate', 'Collector location shared live with the parent', 'Parent confirms safe arrival on their phone', 'Full audit log of every pickup'].map(item => (
                  <li key={item}>
                    <Check size={16} weight="bold" className="icon" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────────────────── */}
      <div className="lp-stats-band">
        <div className="lp-stats-grid">
          {[
            { target: 120,  suffix: '+',   label: 'Schools' },
            { target: 50,   suffix: 'K+',  label: 'Students managed' },
            { target: 30,   suffix: '+',   label: 'Platform modules' },
            { target: 99.9, suffix: '%',   label: 'Uptime' },
          ].map((s, i) => (
            <div key={i} className="lp-stat-cell">
              <div className="lp-stat-num">
                <Counter target={s.target} suffix={s.suffix} />
              </div>
              <div className="lp-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Roles ────────────────────────────────────────────────────── */}
      <section id="roles" style={{ background: 'var(--land-off)' }}>
        <div className="lp-section">
          <p className="lp-section-label lp-fade">Who It's For</p>
          <h2 className="lp-section-h2 lp-fade lp-delay-1">Every role. One system.</h2>
          <p className="lp-section-sub lp-fade lp-delay-2">
            Campusio gives each person exactly what they need — no clutter, no confusion.
          </p>

          <div className="lp-roles-grid">
            {ROLES.map((r, i) => {
              const Icon = r.icon;
              return (
                <div key={i} className={`lp-role-card lp-fade lp-delay-${i + 1}`}>
                  <div className="lp-role-icon"><Icon size={24} weight="duotone" /></div>
                  <div className="lp-role-title">{r.title}</div>
                  <div className="lp-role-desc">{r.description}</div>
                  <ul className="lp-role-list">
                    {r.items.map(item => (
                      <li key={item}><span className="dot" />{item}</li>
                    ))}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <hr className="lp-divider" style={{ margin: 0 }} />

      {/* ── Testimonials ─────────────────────────────────────────────── */}
      <section id="testimonials" style={{ background: 'white' }}>
        <div className="lp-section">
          <p className="lp-section-label lp-fade">Testimonials</p>
          <h2 className="lp-section-h2 lp-fade lp-delay-1">Schools that made the switch.</h2>

          <div className="lp-testimonials-grid">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className={`lp-testimonial lp-fade lp-delay-${i + 1}`}>
                <div className="lp-stars">
                  {Array.from({ length: t.rating }).map((_, j) => (
                    <Star key={j} size={16} weight="fill" color="#D4A017" />
                  ))}
                </div>
                <p className="lp-quote">"{t.content}"</p>
                <div className="lp-cite">
                  <div className="lp-cite-avatar">
                    {t.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <div className="lp-cite-name">{t.name}</div>
                    <div className="lp-cite-role">{t.role} · {t.school}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Comparison ───────────────────────────────────────────────── */}
      <section style={{ background: 'var(--land-off)' }}>
        <div className="lp-section">
          <p className="lp-section-label lp-fade">The Difference</p>
          <h2 className="lp-section-h2 lp-fade lp-delay-1">Why schools move to Campusio.</h2>
          <p className="lp-section-sub lp-fade lp-delay-2">
            Compared to legacy systems and disconnected tools, the gap is significant.
          </p>

          <table className="lp-table lp-fade lp-delay-3">
            <thead>
              <tr>
                <th>Capability</th>
                <th>Legacy systems</th>
                <th>Disconnected tools</th>
                <th style={{ color: 'var(--land-mist)' }}>Campusio</th>
              </tr>
            </thead>
            <tbody>
              {TABLE_ROWS.map((row, i) => (
                <tr key={i}>
                  <td>{row.label}</td>
                  <td><TableIcon value={row.legacy} /></td>
                  <td><TableIcon value={row.tools} /></td>
                  <td><TableIcon value={row.us} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── FAQ ──────────────────────────────────────────────────────── */}
      <section style={{ background: 'white' }}>
        <div className="lp-section" style={{ textAlign: 'center' }}>
          <p className="lp-section-label lp-fade">FAQ</p>
          <h2 className="lp-section-h2 lp-fade lp-delay-1" style={{ maxWidth: 560, margin: '0 auto 12px' }}>
            Common questions
          </h2>

          <div className="lp-faq">
            {FAQS.map((item, i) => (
              <div key={i} className="lp-faq-item lp-fade lp-delay-1">
                <button
                  className="lp-faq-q"
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                >
                  {item.q}
                  <CaretDown
                    size={18}
                    weight="bold"
                    style={{ flexShrink: 0, transition: 'transform 0.25s', transform: openFaq === i ? 'rotate(180deg)' : 'none', color: 'var(--land-moss)' }}
                  />
                </button>
                {openFaq === i && (
                  <div className="lp-faq-a">{item.a}</div>
                )}
              </div>
            ))}
          </div>

          <div style={{ marginTop: 40 }} className="lp-fade">
            <p style={{ fontSize: 14, color: 'var(--land-muted)', marginBottom: 14 }}>Still have questions?</p>
            <button className="lp-btn-ghost" onClick={() => navigate('/contact')}>
              Contact us <ArrowRight size={14} style={{ marginLeft: 4, verticalAlign: 'middle' }} />
            </button>
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────── */}
      <div className="lp-cta-band">
        <h2 className="lp-cta-h2">Ready to modernize your school?</h2>
        <p className="lp-cta-sub">
          Join the schools across Ghana and West Africa that run on Campusio. Start your free 30-day trial today.
        </p>
        <button className="lp-btn-cta" onClick={() => navigate('/contact')}>
          Request a Demo <ArrowRight size={18} weight="bold" />
        </button>
        <div className="lp-cta-note">
          <span className="lp-cta-note-item"><Check size={13} weight="bold" /> Onboarding included</span>
          <span className="lp-cta-note-item"><Check size={13} weight="bold" /> Full feature access</span>
          <span className="lp-cta-note-item"><Check size={13} weight="bold" /> Setup in 48 hours</span>
        </div>
      </div>

      {/* ── Footer ───────────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-footer-grid">
          <div>
            <div style={{ filter: 'brightness(0) invert(1)', opacity: 0.85 }}>
              <LogoWithText size={28} />
            </div>
            <p className="lp-footer-brand-desc">
              Africa's enterprise grade school ERP. Unified, reliable, and built to grow with your institution.
            </p>
          </div>
          <div>
            <div className="lp-footer-col-title">Platform</div>
            <ul className="lp-footer-links">
              <li><button onClick={() => navigate('/features')}>Features</button></li>
              <li><button onClick={() => navigate('/documentation')}>Documentation</button></li>
              <li><button onClick={() => navigate('/contact')}>Request a Demo</button></li>
            </ul>
          </div>
          <div>
            <div className="lp-footer-col-title">Company</div>
            <ul className="lp-footer-links">
              <li><button onClick={() => navigate('/about')}>About Us</button></li>
              <li><button onClick={() => navigate('/contact')}>Contact Us</button></li>
              <li><a href="#">Blog</a></li>
              <li><a href="#">Careers</a></li>
            </ul>
          </div>
          <div>
            <div className="lp-footer-col-title">Legal</div>
            <ul className="lp-footer-links">
              <li><a href="#">Privacy Policy</a></li>
              <li><a href="#">Terms of Service</a></li>
              <li><a href="#">Security</a></li>
              <li><a href="#">Cookie Policy</a></li>
            </ul>
          </div>
        </div>
        <div className="lp-footer-bottom">
          <span className="lp-footer-copy">&copy; 2024 Campusio. All rights reserved.</span>
          <div className="lp-footer-socials">
            <a href="#" aria-label="Twitter"><TwitterLogo size={18} /></a>
            <a href="#" aria-label="LinkedIn"><LinkedinLogo size={18} /></a>
            <a href="#" aria-label="Facebook"><FacebookLogo size={18} /></a>
          </div>
        </div>
      </footer>
    </div>
  );
}
